var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { useMutation } from "@tanstack/react-query";
import { callApiForUseQuery, useApiQuery } from "api/api";
import { isSpecialError } from "api/specialErrorHandler";
import { CommonModal } from "components/common_modal";
import { Icon } from "components/icon";
import * as React from 'react';
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Bcn } from "support/beacon";
const ShareModalContents = ({ diagram, isOpen }) => {
    var _a;
    const { t } = useTranslation();
    const [email, setEmail] = React.useState("");
    const { mutate: mutateShare, isPending: isSharePending } = useMutation({
        mutationFn: (email) => __awaiter(void 0, void 0, void 0, function* () {
            return (callApiForUseQuery("diagram/share", "POST", auth, {
                id: diagram.id,
                email: email,
                role: "viewer",
            }));
        }),
        onSuccess: () => {
            toastr.success(t("cloud.share_success", { email }));
            setEmail("");
            refetch();
        },
        onError: (error) => {
            if (isSpecialError(error)) {
                toastr.error(t("cloud.unshare_error"));
            }
            else {
                toastr.error(error.error);
            }
        }
    });
    const { mutate: mutateUnshare } = useMutation({
        mutationFn: (userId) => __awaiter(void 0, void 0, void 0, function* () {
            return (callApiForUseQuery("diagram/share", "DELETE", auth, {
                id: diagram.id,
                userId
            }));
        }),
        onSuccess: () => {
            toastr.success(t("cloud.unshare_success"));
            refetch();
        },
        onError: (error) => {
            toastr.error(t("cloud.unshare_error"));
        },
    });
    const auth = useSelector((state) => state.auth.state === "logged_in" ? state.auth : undefined);
    const { data, isLoading: isListLoading, isFetching: isListFetching, refetch, error } = useApiQuery("diagram/share", "GET", auth, { id: diagram.id }, { enabled: isOpen });
    let content;
    if (isListLoading) {
        content = null;
    }
    else if (error) {
        content = React.createElement(Error, { error: error });
    }
    else {
        const collaborators = (_a = data === null || data === void 0 ? void 0 : data.sharedWith) !== null && _a !== void 0 ? _a : [];
        if (collaborators.length === 0) {
            content = React.createElement("li", { className: "list-group-item" }, t("cloud.no_collaborators"));
        }
        else {
            content = collaborators.map(c => React.createElement("li", { key: c.userId, className: "list-group-item d-flex justify-content-between align-items-center", style: { gap: "16px" } },
                React.createElement("div", { className: "flex-grow-1 text-truncate", style: { minWidth: 0 } }, c.email),
                React.createElement("div", { className: "text-muted" }, "Viewer"),
                React.createElement("button", { className: "ui-button narrower", onClick: () => handleUnshare(c.userId) },
                    React.createElement(Icon, { fullWidth: true, name: "xmark" }))));
        }
    }
    const handleShare = () => {
        if (!auth || !email)
            return;
        mutateShare(email);
    };
    const handleUnshare = (userId) => {
        if (!auth)
            return;
        mutateUnshare(userId);
    };
    const placeholder = t("cloud.share_email_placeholder");
    return React.createElement(React.Fragment, null,
        React.createElement("div", { className: "d-flex align-middle mb-3" },
            React.createElement("input", { className: "ui-input flex-grow-1 mr-2", value: email, onChange: e => setEmail(e.target.value), placeholder: placeholder }),
            React.createElement("button", { className: "ui-button highlight", onClick: handleShare, disabled: isSharePending || !email },
                isSharePending ? React.createElement("i", { className: "fa fa-spinner fa-spin mr-1" }) : null,
                t("cloud.share_button"))),
        React.createElement("h6", null,
            t("cloud.collaborators"),
            isListFetching && React.createElement("i", { className: "fa fa-spinner fa-spin ml-1" })),
        React.createElement("ul", { className: "list-group" }, content));
};
const Error = ({ error }) => {
    if (isSpecialError(error)) {
        return React.createElement("div", null, "\"ERROR\"");
    }
    return React.createElement("div", null, error.error);
};
export const BetaTooltip = () => (React.createElement("div", { className: "mb-3 text-muted p-2 rounded border border-secondary" },
    "This feature is in beta. \u00A0",
    React.createElement("a", { className: "href-muted", onClick: () => Bcn.ask() }, "Please report any issues or send us feedback.")));
export const ShareModal = (_a) => {
    var { diagram } = _a, modalState = __rest(_a, ["diagram"]);
    const { t } = useTranslation();
    return (React.createElement(CommonModal, Object.assign({}, modalState, { title: t("cloud.share_title", { title: diagram.title }), icon: "fa-share-alt" }),
        React.createElement("div", { className: "p-3", style: { width: "480px" } },
            React.createElement(BetaTooltip, null),
            React.createElement(ShareModalContents, { isOpen: modalState.isOpen, diagram: diagram }))));
};
